/** Shared static joinery and metalwork; deliberately contains no scene or runtime policy. */
import { el, mix, n, poly } from '../../svg';
import { rngFor } from '../../rng';
import type { PieceArt, ResolvedPalette } from '../../contract';
export interface WheelArt extends PieceArt {
  /** Frame-local layers. Rotate wheel about anchors.axle; support stays fixed. */
  readonly layers: { readonly wheel: string; readonly support: string };
}
export function amount(value: unknown, name: string, min = 0, max = 1, integer = false): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < min || value > max || (integer && !Number.isInteger(value))) {
    throw new Error(`mill machinery: ${name} must be ${integer ? 'an integer' : 'finite'} ${min}..${max}`);
  }
  return value;
}
export function flag(value: unknown, name: string): boolean {
  if (typeof value !== 'boolean') throw new Error(`mill machinery: ${name} must be boolean`);
  return value;
}
/** Decorrelate the short channel names in the shared seeded RNG. */
export function grainRng(recipe: { seed: number; id: string }): (channel: string) => number {
  const random = rngFor(recipe);
  return channel => {
    let value = Math.floor(random(channel) * 4294967296) >>> 0;
    value ^= value >>> 16; value = Math.imul(value, 0x85ebca6b);
    value ^= value >>> 13; value = Math.imul(value, 0xc2b2ae35); value ^= value >>> 16;
    return (value >>> 0) / 4294967296;
  };
}
export function line(x1: number, y1: number, x2: number, y2: number, stroke: string, width = 1): string {
  return el('path', { d: `M${n(x1)} ${n(y1)}L${n(x2)} ${n(y2)}`, fill: 'none', stroke, 'stroke-width': width, 'stroke-linecap': 'round' });
}
export function beam(x1: number, y1: number, x2: number, y2: number, width: number, p: ResolvedPalette): string {
  return line(x1, y1, x2, y2, p.outline, width + 2)
    + line(x1, y1, x2, y2, p.woodBase, width)
    + line(x1 - 1, y1, x2 - 1, y2, p.woodLight, Math.max(1, width * 0.16));
}
export function rivet(x: number, y: number, p: ResolvedPalette, radius = 2): string {
  return el('circle', { cx: x, cy: y, r: radius, fill: p.paperBase, stroke: p.outline, 'stroke-width': 0.7 })
    + line(x - radius * 0.4, y - radius * 0.4, x + radius * 0.3, y - radius * 0.4, p.paperLight, 0.65);
}
export function ground(w: number, h: number, p: ResolvedPalette): string {
  return el('path', { d: `M4 ${h}Q8 ${h - 6} 23 ${h - 6}L${w - 22} ${h - 5}Q${w - 7} ${h - 4} ${w - 4} ${h}Z`, fill: p.stoneShadow })
    + line(w * 0.24, h - 2, w * 0.74, h - 2, p.stoneBase, 1);
}
export function ring(cx: number, cy: number, r: number, width: number, p: ResolvedPalette): string {
  return el('circle', { cx, cy, r, fill: 'none', stroke: p.outline, 'stroke-width': width + 2 })
    + el('circle', { cx, cy, r, fill: 'none', stroke: p.woodBase, 'stroke-width': width })
    + el('circle', { cx, cy, r: r - width * 0.26, fill: 'none', stroke: p.woodLight, 'stroke-width': 1.4 });
}
export function gear(cx: number, cy: number, radius: number, teeth: number, p: ResolvedPalette, rust: number): string {
  const points = Array.from({ length: teeth * 4 }, (_, i) => {
    const a = (i / (teeth * 4)) * Math.PI * 2;
    const r = radius * (i % 4 === 1 || i % 4 === 2 ? 1 : 0.83);
    return { x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r };
  });
  return el('path', { d: poly(points), fill: mix(p.stoneBase, p.paperShadow, rust), stroke: p.outline, 'stroke-width': 1.5, 'stroke-linejoin': 'round' })
    + el('circle', { cx, cy, r: radius * 0.61, fill: 'none', stroke: p.paperBase, 'stroke-width': 2 })
    + el('circle', { cx, cy, r: radius * 0.23, fill: p.stoneLight, stroke: p.outline, 'stroke-width': 1 });
}
